import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSubscription } from "@/hooks/useSubscription";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";

interface SubscriptionGuardProps {
  children: React.ReactNode;
}

// Pages accessibles sans abonnement premium
const PUBLIC_ROUTES = [
  "/auth",
  "/auth/callback", 
  "/subscription",
  "/profile",
  "/legal",
  "/privacy",
  "/terms",
  "/presentation"
];

export const SubscriptionGuard = ({ children }: SubscriptionGuardProps) => {
  const { user, loading: authLoading } = useAuth();
  const { hasPremiumAccess, loading: subscriptionLoading } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();

  const isPublicRoute = PUBLIC_ROUTES.some(route => 
    location.pathname === route || location.pathname.startsWith(route + "/")
  );

  useEffect(() => {
    if (authLoading || subscriptionLoading) return;
    if (!user) return;
    if (isPublicRoute) return;

    if (!hasPremiumAccess) {
      console.log("No premium access, redirecting to subscription page");
      navigate("/subscription", { replace: true });
    }
  }, [user, hasPremiumAccess, authLoading, subscriptionLoading, isPublicRoute, navigate]);

  // Afficher un loader pendant le chargement initial (pas de cache)
  if (authLoading || (user && subscriptionLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (user && !hasPremiumAccess && !isPublicRoute) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return <>{children}</>;
};
